import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography
} from "@mui/material";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import {
  adminPageSx,
  contentCardSx,
  mobileActionGroupSx
} from "../utils/adminResponsive";

const initialForm = {
  first_name: "",
  last_name: "",
  reason: "",
  status: "active"
};

export default function BlockedRegistrationNames() {
  const [blockedNames, setBlockedNames] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const activeCount = useMemo(
    () => blockedNames.filter((item) => Boolean(item.is_active)).length,
    [blockedNames]
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const loadBlockedNames = useCallback(async () => {
    setLoading(true);

    try {
      const query = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
      const data = await adminFetch(`/api/admin/blocked-registration-names${query}`, {
        method: "GET"
      });

      if (data?.status) {
        setBlockedNames(data.blocked_names || []);
      } else {
        showSnackbar(data?.message || "Failed to load blocked registration names", "error");
      }
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to load blocked registration names", "error");
    }

    setLoading(false);
  }, [search]);

  useEffect(() => {
    loadBlockedNames();
  }, [loadBlockedNames]);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!String(formData.first_name || "").trim() || !String(formData.last_name || "").trim()) {
      showSnackbar("First name and last name are required", "error");
      return;
    }

    setSaving(true);

    try {
      const endpoint = editingId
        ? `/api/admin/blocked-registration-names/${editingId}`
        : "/api/admin/blocked-registration-names";
      const method = editingId ? "PUT" : "POST";

      const data = await adminFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to save blocked name", "error");
        setSaving(false);
        return;
      }

      showSnackbar(editingId ? "Blocked name updated successfully" : "Blocked name added successfully");
      resetForm();
      await loadBlockedNames();
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }

    setSaving(false);
  };

  const handleDelete = async (id) => {
    const shouldDelete = window.confirm("Delete this blocked registration name?");

    if (!shouldDelete) {
      return;
    }

    try {
      const data = await adminFetch(`/api/admin/blocked-registration-names/${id}`, {
        method: "DELETE"
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to delete blocked name", "error");
        return;
      }

      showSnackbar("Blocked name deleted successfully");
      if (editingId === id) {
        resetForm();
      }
      await loadBlockedNames();
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }
  };

  const blockedNameColumns = [
    {
      key: "first_name",
      label: "First Name",
      render: (item) => item.first_name || "-"
    },
    {
      key: "last_name",
      label: "Last Name",
      render: (item) => item.last_name || "-"
    },
    {
      key: "full_name",
      label: "Full Name",
      render: (item) => item.full_name || [item.first_name, item.last_name].filter(Boolean).join(" ")
    },
    {
      key: "reason",
      label: "Reason",
      render: (item) => item.reason || item.notes || "-"
    },
    {
      key: "status",
      label: "Status",
      render: (item) => item.status || (item.is_active ? "active" : "inactive")
    },
    {
      key: "actions",
      label: "Action",
      isActions: true,
      mobileValueSx: mobileActionGroupSx,
      render: (item) => (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setEditingId(item.id);
              setFormData({
                first_name: item.first_name || "",
                last_name: item.last_name || "",
                reason: item.reason || item.notes || "",
                status: item.status || (item.is_active ? "active" : "inactive")
              });
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => handleDelete(item.id)}
          >
            Delete
          </Button>
        </Stack>
      )
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
        Registration Name Blocklist
      </Typography>

      <Card sx={{ p: 3, mb: 3, ...contentCardSx }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }}>
          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Total Entries
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {blockedNames.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Active Blocks
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {activeCount}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ flex: 1 }}>
            Registration is blocked only when both the first name and last name match an active blocked identity.
          </Alert>
        </Stack>
      </Card>

      <Card sx={{ p: { xs: 2, sm: 4 }, mb: 3, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
          {editingId ? "Update Blocked Name" : "Add Blocked Name"}
        </Typography>

        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="First Name"
              value={formData.first_name}
              onChange={(event) => updateField("first_name", event.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={formData.last_name}
              onChange={(event) => updateField("last_name", event.target.value)}
              fullWidth
              required
            />
          </Stack>
          <TextField
            label="Reason (optional)"
            value={formData.reason}
            onChange={(event) => updateField("reason", event.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            select
            label="Status"
            value={formData.status}
            onChange={(event) => updateField("status", event.target.value)}
            fullWidth
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Registration will only be blocked when both names match this record after normalization.
          </Typography>
          <TextField
            label="Search blocked names"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
            placeholder="Search by first name, last name, or full name"
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              sx={{ bgcolor: Colors.accent, color: "#003300", fontWeight: 700, width: { xs: "100%", sm: "auto" } }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {editingId ? "Update Block" : "Add Block"}
            </Button>
            {editingId ? (
              <Button variant="outlined" onClick={resetForm}>
                Cancel Edit
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Card>

      <Card sx={{ p: 2, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Existing Blocked Names
        </Typography>

        <Box sx={{ position: "relative", minHeight: 320 }}>
          <ResponsiveDataTable
            columns={blockedNameColumns}
            rows={blockedNames}
            loading={loading}
            minWidth={760}
            emptyMinHeight={180}
            emptyMessage="No blocked registration names yet"
            mobileTitle={(item) => item.full_name || [item.first_name, item.last_name].filter(Boolean).join(" ")}
          />

          <TableLoadingOverlay loading={loading} />
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
